import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { createContract, provider, getAccounts, initializeEthers } from './EthersSetup';
import GameRegistryConfig from '../deployedContractsInfo/GameRegistry.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import AddressDisplay from './AddressDisplay';
import { TransactionLink } from './LinkDisplay';

const GameRegistryAdmin = () => {
  const [accounts, setAccounts] = useState([]);
  const [deployer, setDeployer] = useState('');
  const [isDeployer, setIsDeployer] = useState(false);
  const [registryAddress, setRegistryAddress] = useState(contractAddresses.GameRegistry || '');
  const [customRegistryAddress, setCustomRegistryAddress] = useState('');
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [txHash, setTxHash] = useState('');

  // Form state for adding/editing games
  const [editingGame, setEditingGame] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    gameAddress: '',
    paramCount: '',
    description_game: '',
    description_params: '',
    url: ''
  });

  // Load accounts
  const loadAccounts = useCallback(async () => {
    try {
      // Initialize Ethers first (auto-connect to previously used wallet)
      await initializeEthers();
      const currentAccounts = await getAccounts();
      setAccounts(currentAccounts);
    } catch (err) {
      console.error('Error getting accounts:', err);
      setAccounts([]);
    }
  }, []);

  // Load accounts on mount
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Get registry contract
  const getRegistryContract = useCallback(async () => {
    const address = useCustomAddress ? customRegistryAddress : registryAddress;
    if (!address || !ethers.utils.isAddress(address)) {
      throw new Error('Invalid registry address');
    }
    return await createContract(address, GameRegistryConfig.abi, true);
  }, [useCustomAddress, customRegistryAddress, registryAddress]);

  // Load deployer and check if current account is deployer
  const loadDeployer = useCallback(async () => {
    try {
      const contract = await getRegistryContract();
      const deployerAddress = await contract.deployer();
      setDeployer(deployerAddress);
      const currentAccount = accounts[0] || '';
      setIsDeployer(currentAccount.toLowerCase() === deployerAddress.toLowerCase());
    } catch (err) {
      console.error('Error loading deployer:', err);
      setError('Failed to load deployer information');
      setIsDeployer(false);
    }
  }, [accounts, getRegistryContract]);

  // Load all games from registry
  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      const contract = await createContract(
        useCustomAddress ? customRegistryAddress : registryAddress,
        GameRegistryConfig.abi,
        false
      );

      const gameNames = await contract.getAllGameNames();
      const gameInfos = await contract.getAllGames();

      const gamesData = gameNames.map((name, index) => ({
        name,
        ...gameInfos[index]
      }));

      setGames(gamesData);
      setError('');
    } catch (err) {
      console.error('Error loading games:', err);
      setError('Failed to load games from registry');
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, [useCustomAddress, customRegistryAddress, registryAddress]);

  // Load data when component mounts or registry address changes
  useEffect(() => {
    if (accounts[0] && (registryAddress || customRegistryAddress)) {
      loadDeployer();
      loadGames();
    }
  }, [accounts, registryAddress, customRegistryAddress, useCustomAddress, loadDeployer, loadGames]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isDeployer) {
      setError('Only the deployer can manage game registry');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setTxHash('');

      const contract = await getRegistryContract();

      // Validate inputs
      if (!ethers.utils.isAddress(formData.gameAddress)) {
        throw new Error('Invalid game contract address');
      }

      const paramCount = parseInt(formData.paramCount);
      if (isNaN(paramCount) || paramCount < 0) {
        throw new Error('Parameter count must be a non-negative number');
      }

      // Register/update game
      const tx = await contract.registerGame(
        formData.name,
        formData.gameAddress,
        paramCount,
        formData.description_game,
        formData.description_params,
        formData.url
      );

      setTxHash(tx.hash);
      setSuccess(`Transaction submitted: ${tx.hash}`);

      // Wait for confirmation
      await tx.wait();
      setSuccess('Game successfully registered/updated!');

      // Reload games and reset form
      await loadGames();
      resetForm();

    } catch (err) {
      console.error('Error registering game:', err);
      setError(err.message || 'Failed to register game');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      gameAddress: '',
      paramCount: '',
      description_game: '',
      description_params: '',
      url: ''
    });
    setEditingGame(null);
  };

  // Edit existing game
  const editGame = (game) => {
    setFormData({
      name: game.name,
      gameAddress: game.gameAddress,
      paramCount: game.paramCount.toString(),
      description_game: game.description_game,
      description_params: game.description_params,
      url: game.url
    });
    setEditingGame(game.name);
  };

  // Set game to "deleted" state (safe deletion)
  const setGameToZero = (game) => {
    setFormData({
      name: game.name,
      gameAddress: '0x0000000000000000000000000000000000000000',
      paramCount: '0',
      description_game: '[DELETED]',
      description_params: '[DELETED]',
      url: ''
    });
    setEditingGame(game.name);
  };

  // Handle registry address change
  const handleRegistryAddressChange = () => {
    if (useCustomAddress && customRegistryAddress) {
      setRegistryAddress(customRegistryAddress);
    } else {
      setRegistryAddress(contractAddresses.GameRegistry || '');
    }
  };

  const currentAccount = accounts[0] || '';

  if (!currentAccount) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Wallet Connection</h2>
          <p className="text-yellow-700 mb-2">
            {accounts.length === 0
              ? "Please connect your wallet to access the GameRegistry admin panel."
              : "Wallet connected but no accounts available."
            }
          </p>
          {provider && (
            <div className="text-sm text-gray-600">
              <p><strong>Provider status:</strong> Connected</p>
              <p><strong>Accounts found:</strong> {accounts.length}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isDeployer && deployer) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-700 mb-2">Only the deployer can access this admin panel.</p>
          <div className="text-sm space-y-1">
            <p><strong>Your address:</strong> <AddressDisplay value={currentAccount} /></p>
            <p><strong>Deployer address:</strong> <AddressDisplay value={deployer} /></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">GameRegistry Admin Panel</h1>
        <p className="text-gray-600 mb-4">Manage game registrations in the GameRegistry contract</p>

        {/* Access Info */}
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-800">✅ Admin Access Confirmed</h3>
              <p className="text-green-700 text-sm">Connected as deployer: <AddressDisplay value={currentAccount} /></p>
            </div>
          </div>
        </div>

        {/* Registry Address Configuration */}
        <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">GameRegistry Contract Address</h3>

          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="default-address"
                checked={!useCustomAddress}
                onChange={() => {
                  setUseCustomAddress(false);
                  setRegistryAddress(contractAddresses.GameRegistry || '');
                }}
                className="h-4 w-4 text-blue-600"
              />
              <label htmlFor="default-address" className="text-sm font-medium text-gray-700">
                Use default address from deployment
              </label>
            </div>

            {!useCustomAddress && (
              <div className="ml-7 p-2 bg-blue-50 rounded text-sm font-mono text-blue-800">
                {contractAddresses.GameRegistry || 'Not found in deployment config'}
              </div>
            )}

            <div className="flex items-center space-x-3">
              <input
                type="radio"
                id="custom-address"
                checked={useCustomAddress}
                onChange={() => setUseCustomAddress(true)}
                className="h-4 w-4 text-blue-600"
              />
              <label htmlFor="custom-address" className="text-sm font-medium text-gray-700">
                Use custom address
              </label>
            </div>

            {useCustomAddress && (
              <div className="ml-7 space-y-2">
                <input
                  type="text"
                  value={customRegistryAddress}
                  onChange={(e) => setCustomRegistryAddress(e.target.value)}
                  placeholder="0x..."
                  className="w-full p-2 border border-gray-300 rounded text-sm font-mono"
                />
                <button
                  onClick={handleRegistryAddressChange}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Apply Custom Address
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
            <strong>Current registry:</strong>
            <span className="font-mono ml-2">{registryAddress || 'Not set'}</span>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
            <p className="text-green-700">{success}</p>
            {txHash && (
              <div className="mt-2">
                <TransactionLink value={txHash} provider={provider} />
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Game Form */}
        <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {editingGame ? `Edit Game: ${editingGame}` : 'Add New Game'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Game Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded"
                  required
                  placeholder="e.g., TicTacToe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Address</label>
                <input
                  type="text"
                  value={formData.gameAddress}
                  onChange={(e) => setFormData({...formData, gameAddress: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm"
                  required
                  placeholder="0x..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parameter Count</label>
                <input
                  type="number"
                  value={formData.paramCount}
                  onChange={(e) => setFormData({...formData, paramCount: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Game Description</label>
              <textarea
                value={formData.description_game}
                onChange={(e) => setFormData({...formData, description_game: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded h-20"
                placeholder="Describe the game..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parameters Description</label>
              <textarea
                value={formData.description_params}
                onChange={(e) => setFormData({...formData, description_params: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded h-20"
                placeholder="Describe the game parameters..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading || !isDeployer}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Processing...' : editingGame ? 'Update Game' : 'Add Game'}
              </button>

              {editingGame && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Current Games List */}
        <div className="bg-white border border-gray-300 rounded-lg">
          <div className="p-4 bg-gray-50 border-b border-gray-300">
            <h3 className="text-lg font-semibold text-gray-800">Current Registered Games</h3>
            <button
              onClick={loadGames}
              className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          <div className="overflow-x-auto">
            {games.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {loading ? 'Loading games...' : 'No games registered'}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Address</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Params</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Description</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">URL</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {games.map((game, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">{game.name}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <AddressDisplay value={game.gameAddress} />
                      </td>
                      <td className="px-4 py-2 text-center">{game.paramCount.toString()}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={game.description_game}>
                        {game.description_game || '-'}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {game.url ? (
                          <a href={game.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Link
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2">
                        <div className="space-x-2">
                          <button
                            onClick={() => editGame(game)}
                            className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                            disabled={!isDeployer}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setGameToZero(game)}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                            disabled={!isDeployer}
                            title="Set game to deleted state (address = 0x0, safe deletion)"
                          >
                            Set to 0
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameRegistryAdmin;